import styled from "styled-components";
import { device } from "../../constants";

export const Section = styled.section`
  background: ${({ theme }) => theme.colors["neutral-08"]};
  position: relative;
  overflow: hidden;
`;

export const BackgroundImage = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0) 60.3%,
      rgb(0, 0, 0) 100%
    );
  }
`;

export const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  padding: 32px 20px;

  @media ${device.tablet} {
    gap: 40px;
    padding: 40px 40px;
  }

  @media ${device.laptop} {
    gap: 56px;
    padding: 40px 80px;
  }
`;

export const TitleWrapper = styled.div`
  text-align: center;
  width: 100%;
`;

export const Title = styled.h2`
  margin: 0;
  font-size: 24px;
  line-height: 32px;
  color: ${({ theme }) => theme.colors["vivid-02"]};
  text-align: center;

  font-weight: 400;

  strong {
    font-weight: 600;
    color: ${({ theme }) => theme.colors["vivid-02"]};
  }

  @media ${device.tablet} {
    font-size: 32px;
    line-height: 40px;
  }

  @media ${device.laptop} {
    font-size: 40px;
    line-height: 48px;
  }
`;

export const Description = styled.div`
  margin-top: 16px;
  text-align: center;
  font-size: 16px;
  line-height: 28px;
  color: white;

  .highlight {
    font-weight: 700;
    color: #C4F2A4; /* ds-brand-green-300 */
  }

  @media ${device.tablet} {
    font-size: 14px;
    line-height: 24px;
  }

  @media ${device.laptop} {
    font-size: 20px;
    line-height: 32px;
  }
`;

export const FormCard = styled.div`
  background: ${({ theme }) => theme.colors["background-02"]};
  border-radius: 8px;
  padding: 16px;
  width: 100%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  color: #ffffff;

  /* Override global * { color: #210E3F } */
  *, *::before, *::after {
    color: inherit;
  }

  .field-error {
    color: #ef4444 !important;
  }

  @media ${device.tablet} {
    padding: 24px;
    gap: 24px;
  }
`;

export const StyledInput = styled.input`
  width: 100%;
  height: 48px;
  border: 1px solid #ffffff;
  border-radius: 8px;
  padding: 0 16px;
  background: transparent;
  color: #ffffff;
  font-size: 16px;
  line-height: 20px;

  &::placeholder {
    color: ${({ theme }) => theme.colors["neutral-02"]};
  }

  &:focus {
    outline: none;
  }

  &[data-error="true"] {
    border-color: #ef4444;
  }

  @media ${device.laptop} {
    height: 56px;
    font-size: 18px;
    line-height: 24px;
  }
`;

export const StyledTextarea = styled.textarea`
  width: 100%;
  height: 140px;
  border: 1px solid #ffffff;
  border-radius: 12px;
  padding: 16px;
  background: transparent;
  color: #ffffff;
  font-size: 16px;
  line-height: 20px;
  resize: none;

  &::placeholder {
    color: ${({ theme }) => theme.colors["neutral-02"]};
  }

  &:focus {
    outline: none;
  }

  &[data-error="true"] {
    border-color: #ef4444;
  }

  @media ${device.laptop} {
    font-size: 18px;
    line-height: 24px;
  }
`;

export const StyledPhoneInputWrapper = styled.div`
  width: 100%;

  .react-international-phone-input-container {
    width: 100%;
    display: flex;
    height: 48px;

    @media ${device.laptop} {
      height: 56px;
    }
  }

  .react-international-phone-input-container .react-international-phone-input {
    width: 100%;
    height: 100%;
    background-color: transparent !important;
    color: #ffffff !important;
    border: 1px solid #ffffff;
    border-left: none;
    border-radius: 0 8px 8px 0;
    padding: 0 16px;
    font-size: 16px;
    flex: 1;

    &::placeholder {
      color: ${({ theme }) => theme.colors["neutral-02"]};
    }

    &:focus {
      outline: none;
      border-color: #ffffff;
    }

    @media ${device.laptop} {
      font-size: 18px;
    }
  }

  .react-international-phone-country-selector-button {
    height: 100%;
    background-color: transparent !important;
    border: 1px solid #ffffff;
    border-right: none;
    border-radius: 8px 0 0 8px !important;
    padding: 0 8px;
    min-width: 70px;

    &:hover {
      background-color: ${({ theme }) => theme.colors["neutral-07"]} !important;
    }
  }

  .react-international-phone-country-selector-button__button-content {
    color: #ffffff;
  }

  .react-international-phone-country-selector-button__dropdown-arrow {
    border-color: #ffffff transparent transparent;
  }

  &[data-error="true"] {
    .react-international-phone-input {
      border-color: #ef4444;
    }
    .react-international-phone-country-selector-button {
      border-color: #ef4444;
    }
  }
`;

export const SubmitButton = styled.button`
  display: flex;
  height: 40px;
  padding: 12px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 100px;
  border: 1px solid #9fe870;
  background: rgba(159, 232, 112, 0.1);
  color: #9fe870;
  cursor: pointer;
  transition: opacity 0.2s;

  @media ${device.tablet} {
    height: 48px;
    padding: 8px 20px;
    gap: 16px;
    background: transparent;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  span {
    font-size: 14px;
    line-height: 16px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0;
    white-space: nowrap;
    color: #9fe870;

    @media ${device.tablet} {
      line-height: 24px;
      letter-spacing: 1.2px;
    }

    @media ${device.laptop} {
      font-size: 16px;
    }
  }

  svg {
    width: 16px;
    height: 16px;
    color: #9fe870;
    fill: #9fe870;
    flex-shrink: 0;
  }
`;

export const LocationsWrapper = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;

  @media ${device.laptopM} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 0;
  }
`;

export const LocationsText = styled.p`
  margin: 0;
  font-weight: 400;
  color: white;
  text-align: center;
  font-size: 16px;
  line-height: 28px;
  max-width: 377px;

  @media ${device.tablet} {
    font-size: 14px;
    line-height: 22px;
    max-width: 478px;
    width: 100%;
  }

  @media ${device.laptopM} {
    font-size: 20px;
    line-height: 32px;
    text-align: left;
    flex-shrink: 0;
    max-width: 377px;
    width: auto;
  }
`;

export const LocationsGrid = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;

  @media ${device.tablet} {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0;
    width: 100%;
  }

  @media ${device.laptop} {
    justify-content: flex-end;
    flex: 1;
  }
`;

export const Location = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 8px;
  width: 243px;
  flex-shrink: 1;

  @media ${device.tablet} {
    width: auto;
    flex: 1;
  }

  @media ${device.laptopM} {
    width: 243px;
    flex: none;
  }

  @media ${device.laptop} {
    & + & {
      padding-left: 32px;

      &::before {
        content: "";
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 1px;
        height: 104px;
        background: ${({ theme }) => theme.colors["neutral-03"]};
      }
    }
  }
`;

export const Flag = styled.img`
  width: 24px;
  height: 24px;
`;

export const LocationTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  line-height: 40px;
  letter-spacing: 1.4px;
  white-space: nowrap;
  color: white;
  text-align: center;
`;

export const LocationPhone = styled.div`
  font-size: 14px;
  line-height: 16px;
  letter-spacing: 1.4px;
  color: ${({ theme }) => theme.colors["neutral-02"]};
  text-align: center;
`;

export const ContactIcons = styled.div`
  display: flex;
  gap: 24px;
  margin-top: 12px;
  align-items: center;
`;

export const ContactIcon = styled.a`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 4px;
  color: #ffffff !important;

  &:hover {
    opacity: 0.8;
    transform: scale(1.1);
  }

  svg {
    width: 24px;
    height: 24px;
    color: #ffffff !important;
    fill: #ffffff !important;
  }

  &:nth-child(2) svg {
    width: 20px;
    height: 20px;
  }
`;
